import { useQuery } from '@tanstack/react-query';
import dataService from 'services/dataService';
import { useAppDispatch } from 'store';
import useUser from 'utils/useUser';
import { showResult } from 'infrastructure/hooks/utils';
import { useEffect } from 'react';

import { apiUrlGenerator } from '../../use-api';

import type { TReadingGroupWithNone } from 'store/linked-call-readings';
import type { ReadingStatus } from 'infrastructure/enums';
import type { IPartialPaginationMeta } from 'infrastructure/interfaces';
import type { IRpmPatientActivity } from 'infrastructure/interfaces/i-rpm-patient-activity';

interface Props {
  patientGuid: string;
  clinicGuid?: string;
  reviewed?: boolean;
  deviceIds?: string;
  reverse?: boolean;
  onlyIsErroneous?: boolean;
  meta: IPartialPaginationMeta;
  allowCriticals?: boolean;
  startTime?: number;
  endTime?: number;
  status?: ReadingStatus[];
  readingId?: string;
  applyLinkedCalls?: boolean;
  readingGroups?: TReadingGroupWithNone[];
}

export type TActivityFilter = {
  patientGuid: string;
  clinicGuid?: string;
  startTime?: number;
  endTime?: number;
  reviewed?: boolean;
  deviceIds?: string;
  userType: string;
  reverse?: boolean;
  allowCriticals?: boolean;
  onlyIsErroneous?: boolean;
  status?: ReadingStatus[];
  readingId?: string;
  applyLinkedCalls?: boolean;
  readingGroup?: TReadingGroupWithNone[];
} & IPartialPaginationMeta;

export type TRpmPatientActivityQueryKey = [string, TActivityFilter];

export const ACTIVITY_PER_PAGE = 100;

const getRpmPatientActivity = async ({
  patientGuid,
  ...filter
}: TActivityFilter) => {
  if (filter.readingGroup?.length === 0) return { count: 0, data: [] };

  const url = `core/patients/${patientGuid}/readings`;
  const { data, error } = await dataService.getList<IRpmPatientActivity>(
    apiUrlGenerator(url, filter),
  );

  if (error) {
    showResult(error);
    throw new Error(error);
  }

  return data;
};

export const useRpmPatientActivityKey = (
  filter: TActivityFilter,
): TRpmPatientActivityQueryKey => ['getRpmPatientActivity', filter];

export const useRpmPatientActivity = ({
  patientGuid,
  clinicGuid,
  deviceIds,
  reviewed,
  reverse,
  onlyIsErroneous,
  meta,
  allowCriticals,
  startTime,
  endTime,
  status,
  readingId,
  applyLinkedCalls,
  readingGroups,
}: Props) => {
  const { userType } = useUser();

  const {
    activity: { setActivities, setTotalCountAndTotalPages },
  } = useAppDispatch();

  const filter: TActivityFilter = {
    ...meta,
    patientGuid,
    clinicGuid,
    startTime,
    endTime,
    reviewed,
    deviceIds,
    userType,
    reverse,
    allowCriticals,
    onlyIsErroneous,
    items: meta.items ?? ACTIVITY_PER_PAGE,
    status,
    readingId,
    applyLinkedCalls,
    readingGroup: readingGroups,
  };

  const queryKey = useRpmPatientActivityKey(filter);

  const { data: activity, ...other } = useQuery({
    queryKey,
    queryFn: async () => getRpmPatientActivity(filter),
    enabled: Boolean(patientGuid),
    keepPreviousData: true,
  });

  const totalCount = activity?.count || 0;
  const totalPages =
    totalCount === 0 ? 0 : Math.ceil(totalCount / ACTIVITY_PER_PAGE);

  useEffect(() => {
    setActivities(activity?.data || []);
  }, [activity?.data]);

  useEffect(() => {
    setTotalCountAndTotalPages({
      totalCount,
      totalPages,
    });
  }, [totalPages, totalCount]);

  return {
    activity,
    queryKey,
    startTime,
    endTime,
    ...other,
  };
};
